import { BridgeTransaction } from '../../types/api';
import { getExplorerUrl, getChainName, getExplorerName } from '../../lib/utils';

interface TransactionInfoProps {
  transaction: BridgeTransaction;
}

const TransactionInfo: React.FC<TransactionInfoProps> = ({ transaction }) => {
  const formatTimestamp = (timestamp: string | number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatAmount = (amount: string) => {
    // Convert from wei/gwei to ETH by dividing by 1e18
    const num = parseFloat(amount) / 1e18;
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toFixed(6);
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'processing': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'failed': 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
        statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800 border-gray-200'
      }`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getRiskBadge = (risk: string) => {
    const riskClasses = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        riskClasses[risk as keyof typeof riskClasses] || 'bg-gray-100 text-gray-800'
      }`}>
        {risk.charAt(0).toUpperCase() + risk.slice(1)} Risk
      </span>
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // In a real app, you might show a toast notification here
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Transaction Details</h3>
          <div className="flex items-center space-x-3">
            {getStatusBadge(transaction.status)}
            {getRiskBadge(transaction.riskLevel)}
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide">Basic Information</h4>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Transaction ID</label>
                <div className="flex items-center space-x-2 mt-1">
                  <code className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded">
                    {transaction.id}
                  </code>
                  <button
                    onClick={() => copyToClipboard(transaction.id)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">Amount</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatAmount(transaction.amount)} PFVS
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">Direction</label>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-sm text-gray-900">
                    {getChainName(transaction.srcChainId)}
                  </span>
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  <span className="text-sm text-gray-900">
                    {getChainName(transaction.destChainId)}
                  </span>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">RequestTimestamp</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatTimestamp(transaction.requestTimestamp * 1000)}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">Created</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatTimestamp(transaction.createdAt)}
                </p>
              </div>

              {transaction.executeTxHash && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Completed</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatTimestamp(transaction.updatedAt)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Source Transaction */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide">Source Transaction</h4>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Source Hash</label>
                <div className="flex items-center space-x-2 mt-1">
                  <code className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded break-all">
                    {transaction.requestTxHash}
                  </code>
                  <button
                    onClick={() => copyToClipboard(transaction.requestTxHash)}
                    className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">Source Block</label>
                <p className="text-sm text-gray-900 mt-1">
                  {transaction.requestBlockNumber}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">From Address</label>
                <div className="flex items-center space-x-2 mt-1">
                  <code className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded break-all">
                    {transaction.sender}
                  </code>
                  <button
                    onClick={() => copyToClipboard(transaction.sender)}
                    className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">To Address</label>
                <div className="flex items-center space-x-2 mt-1">
                  <code className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded break-all">
                    {transaction.receiver}
                  </code>
                  <button
                    onClick={() => copyToClipboard(transaction.receiver)}
                    className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {transaction.executeTxHash && (
                <>
                  <div className="pt-4 border-t border-gray-200">
                    <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Target Transaction</h5>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500">Target Hash</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <code className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded break-all">
                        {transaction.executeTxHash}
                      </code>
                      <button
                        onClick={() => copyToClipboard(transaction.executeTxHash || '')}
                        className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {transaction.executeBlockNumber && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Target Block</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {transaction.executeBlockNumber}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Processing Information */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide mb-4">Processing Information</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500">Processing Time</label>
              <p className="text-sm text-gray-900 mt-1">
                {transaction.executeTimestamp && transaction.requestTimestamp ? `${transaction.executeTimestamp - transaction.requestTimestamp}s` : 'N/A'}
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">Total Approvals</label>
              <p className="text-sm text-gray-900 mt-1">
                {transaction.totalApprovals || 'N/A'}
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">Required Approvals</label>
              <p className="text-sm text-gray-900 mt-1">
                {transaction.requiredApprovals || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* External Links */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide mb-4">Explorer Links</h4>
          
          <div className="flex flex-wrap gap-3">
            <a
              href={getExplorerUrl(transaction.srcChainId, transaction.requestTxHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View on {getExplorerName(transaction.srcChainId)}
            </a>
            
            {transaction.executeTxHash && (
              <a
                href={getExplorerUrl(transaction.destChainId, transaction.executeTxHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Target on {getExplorerName(transaction.destChainId)}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionInfo; 